export interface StockAllocation {
  id: string;
  contrato_id: string;
  destino_nome: string;
  marca: string;
  potencia_w: number;
  modelo: string;
  quantidade: number;
  status: 'Reservada' | 'Separada' | string;
  data_alocacao: string;
}

export interface StockAllocationsResponse {
  allocations: StockAllocation[];
}

export interface StockFeedError {
  error: string;
  status: number;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export class StockAllocationsService {
  private static cache: StockAllocation[] | null = null;
  private static cacheTime: number = 0;
  private static readonly CACHE_TTL = 60_000;

  static clearCache(): void {
    this.cache = null;
    this.cacheTime = 0;
  }

  static async fetchAllocations(force = false): Promise<{ data: StockAllocation[] | null; error: StockFeedError | null }> {
    if (!force && this.cache && Date.now() - this.cacheTime < this.CACHE_TTL) {
      return { data: this.cache, error: null };
    }

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/stock-allocations`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (res.status === 401) {
        const body = await res.json().catch(() => ({}));
        return {
          data: null,
          error: {
            error: body.error || 'Senha de acesso incorreta. Confira o STOCK_FEED_SECRET nos dois apps.',
            status: 401,
          },
        };
      }

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        return {
          data: null,
          error: {
            error: body.error || 'Nao foi possivel obter as alocacoes do estoque.',
            status: res.status,
          },
        };
      }

      const json: StockAllocationsResponse = await res.json();
      const allocations = json.allocations || [];

      this.cache = allocations;
      this.cacheTime = Date.now();

      return { data: allocations, error: null };
    } catch {
      return {
        data: null,
        error: {
          error: 'Nao foi possivel conectar ao app de estoque. Tente novamente.',
          status: 0,
        },
      };
    }
  }

  static groupByContract(allocations: StockAllocation[]): Map<string, StockAllocation[]> {
    const map = new Map<string, StockAllocation[]>();
    for (const a of allocations) {
      const list = map.get(a.contrato_id) || [];
      list.push(a);
      map.set(a.contrato_id, list);
    }
    return map;
  }

  static formatAllocationsLabel(allocs: StockAllocation[] | undefined): string {
    if (!allocs || allocs.length === 0) return 'Sem placa definida';
    return allocs
      .map((a) => `${a.quantidade}x ${a.marca} ${a.potencia_w}W`)
      .join(' + ');
  }

  static calculateAllocatedKwp(allocs: StockAllocation[] | undefined): number {
    if (!allocs || allocs.length === 0) return 0;
    return allocs.reduce((sum, a) => sum + a.quantidade * a.potencia_w, 0) / 1000;
  }
}
